/**
 * Turns a failed request into something worth showing a person.
 *
 * Warp Drive's fetch handler throws an error whose `message` is only
 * `[403 Forbidden] PATCH (cors) - https://api…/coachProfiles/123`. The reason
 * the request failed is in the response body, which it parses onto
 * `error.content` (and, when the body carried a JSON:API `errors` array, onto
 * `error.errors` via `AggregateError`). Nothing read either, so every save
 * failure showed the URL and the real cause — "LinkedIn Profile Link must be a
 * string" — was only visible in the network tab.
 */

/** A JSON:API error object as ll-api returns it. */
interface JsonApiError {
  status?: string | number;
  code?: string;
  title?: string;
  /**
   * Per JSON:API this is a string, and most of ll-api's handlers send one.
   * The Joi validation path does not: it sends the raw validation array, so
   * `detail` is a list of `{ message, path }` objects there.
   */
  detail?: string | JoiDetail[];
}

/** One Joi failure, as it appears inside `detail` on a validation error. */
interface JoiDetail {
  message?: string;
  path?: (string | number)[];
  type?: string;
}

interface RequestError {
  content?: unknown;
  errors?: unknown;
  status?: number;
  statusText?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Pulls the JSON:API error objects off whichever property carried them. */
function errorObjects(error: unknown): JsonApiError[] {
  if (!isRecord(error)) {
    return [];
  }

  const candidate = error as RequestError;

  // AggregateError, when the body had an `errors` array.
  if (Array.isArray(candidate.errors)) {
    return candidate.errors.filter(isRecord);
  }

  // The parsed body, whether or not Warp Drive found an `errors` array in it.
  const content = candidate.content;
  if (Array.isArray(content)) {
    return content.filter(isRecord);
  }
  if (isRecord(content) && Array.isArray(content.errors)) {
    return content.errors.filter(isRecord);
  }

  return [];
}

/** "linkedInProfileLink" / "testimonials.0.author", or "" at the top level. */
function fieldPath(detail: JoiDetail): string {
  return Array.isArray(detail.path) ? detail.path.join('.') : '';
}

/**
 * Every distinct human-readable reason the request failed, most specific first.
 *
 * Validation messages beat the title they arrive under: "Param validation
 * failed" tells a coach nothing, while the messages beneath it name the field.
 */
export function apiErrorMessages(error: unknown): string[] {
  const messages: string[] = [];

  for (const item of errorObjects(error)) {
    const { detail, title } = item;

    if (Array.isArray(detail)) {
      for (const entry of detail) {
        const message = entry?.message?.trim();
        if (message) {
          messages.push(message);
        }
      }
      continue;
    }

    const text = typeof detail === 'string' ? detail.trim() : '';
    if (text) {
      messages.push(text);
      continue;
    }

    const heading = title?.trim();
    if (heading) {
      messages.push(heading);
    }
  }

  // Distinct, order preserved - the same message often repeats across errors.
  return [...new Set(messages)];
}

/**
 * The same reasons, each labelled with the field it came from where the API
 * said. For a form, "linkedInProfileLink: ... must be a string" is the
 * difference between a coach knowing which box to fix and not.
 */
export function apiErrorFieldMessages(
  error: unknown
): { field: string; message: string }[] {
  const out: { field: string; message: string }[] = [];

  for (const item of errorObjects(error)) {
    if (!Array.isArray(item.detail)) {
      continue;
    }
    for (const entry of item.detail) {
      const message = entry?.message?.trim();
      if (message) {
        out.push({ field: fieldPath(entry), message });
      }
    }
  }

  return out;
}

/**
 * Joins reasons so several read as several sentences.
 *
 * Joi messages carry no terminal punctuation, so concatenating them ran two
 * failures together: `"Degree" is required "Year of Graduation" must be a
 * number`. Only the separators are added - the messages are the API's words.
 */
function asSentences(messages: string[]): string {
  return messages
    .map((message) => (/[.!?]$/.test(message) ? message : `${message}.`))
    .join(' ');
}

/**
 * One string to put in an alert.
 *
 * Falls back to `fallback` rather than `error.message`, because that message is
 * the bare status line and a URL - the thing this exists to stop showing.
 */
export function formatApiError(error: unknown, fallback: string): string {
  const messages = apiErrorMessages(error);

  if (messages.length) {
    return asSentences(messages);
  }

  // A thrown Error the app raised itself ("Coaching plan not found") is already
  // written for a person; a Warp Drive request error's message is not.
  if (error instanceof Error && !(error as RequestError).status) {
    const message = error.message.trim();
    if (message) {
      return message;
    }
  }

  return fallback;
}
